<template>
  <div class="col-sm-12 nopads">
    <div class="px-2 py-1" style="background-color: #FFFFFF; border-radius: .5em">
      <h3 class="editor-header">{{ editOld ? $t('delivery_note.edit') : $t('delivery_note.add_new') }}</h3>
      <form
          v-if="noteUpdate"
          id="delivery-note-editor"
          @submit.prevent="onSubmit"
          @reset="onReset"
      >
        <div v-if="(!noteUpdate.id || noteUpdate.id < 0) && setCreated" class="row">
          <div class="col-sm-6">
            <span class="search-title">
              {{ $t('delivery_note.date') }}
            </span>
            <date-time-picker
              v-model:date="createdTime"
              class="routa-input"
              format="dd.MM.yyyy HH:mm"
              :enableTimePicker="true"
              :set-time-button-visible="true"
              :placeholder="$t('observations_videos.search_input_format')"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-sm-6">
            <span class="span-title">{{ $t('delivery_note.contract') }}</span>
              <select
                  v-model="contract"
                  class="form-select form-select-sm"
                  :disabled="!!loading"
                  @change="selectContract"
              >
                  <option :value="null">
                      {{ $t('delivery_note.form.select_contract') }}
                  </option>
                  <option
                      v-for="contract in contracts"
                      :key="contract.id"
                      :value="contract.id"
                  >
                      {{ contract.name }}
                  </option>
              </select>
              <div v-if="showOnlyMyContractsOption" class="form-check">
                  <input
                      type="checkbox"
                      class="form-check-input"
                      style="margin-right: .5em"
                      v-model="onlyUserOwnedContracts"
                      id="onlyUserOwnedContractsCheckbox"
                  />
                  <label class="form-check-label" for="onlyUserOwnedContractsCheckbox">
                      {{ $t('contracts.only_user_owned_contracts') }}
                  </label>
              </div>
          </div>
          <div class="col-sm-6">
              <contract-order-multiselect
                  ref="orderFilter"
                  :contract-ids="contract ?  [contract] : []"
                  :single-mode="true"
                  :open-only="true"
                  :pre-selected-order="noteUpdate.order ? noteUpdate.order.id : null"
                  @orders-selected="setOrder"
              />
          </div>
        </div>
        <div
          v-if="showStationBlock"
          class="row"
        >
          <div class="col-sm-6">
            <span class="span-title">{{ $t('delivery_note.material') }}</span>
              <select
                  v-model="noteUpdate.station_material.id"
                  class="form-select form-select-sm"
                  :disabled="!station || materials.length < 1 || !!loading"
              >
                  <option :value="-1">
                      {{ $t('delivery_note.form.select_material') }}
                  </option>
                  <option
                      v-if="materials.length < 1 && (noteUpdate.station_material.id > 0)"
                      :value="noteUpdate.station_material.id"
                  >
                      {{
                          noteUpdate.station_material.material.name + ' (' + noteUpdate.station_material.type_test_code + ')'
                      }}
                  </option>
                  <option
                      v-for="material in materials"
                      :key="material.id"
                      :value="material.id"
                  >
                      {{ material.material.name + ' (' + material.type_test_code + ')' }}
                  </option>
              </select>
          </div>
          <div class="col-sm-6">
            <span class="span-title">{{ $t('delivery_note.amount') }}</span>
            <div class="row">
              <div class="col-7">
                  <input
                      type="number"
                      class="form-control form-control-sm"
                      :value="noteUpdate.amount"
                      step="0.01"
                      :disabled="!!loading"
                      @input="i => noteUpdate.amount = Number(i.target.value)"
                  />
              </div>
              <div class="col-5">
                  <select
                      v-model="unit"
                      class="form-select form-select-sm"
                      :disabled="!!loading"
                  >
                      <option :value="-1">
                          {{ $t('delivery_note.form.select_unit') }}
                      </option>
                      <option
                          v-for="unit in units"
                          :key="unit.value"
                          :value="unit.value"
                          :disabled="!!loading"
                      >
                          {{ unit.value }}
                      </option>
                  </select>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="showStorageBlock && !editOld"
          class="row"
        >
          <div class="col-sm-6">
            <span class="span-title">{{ $t('delivery_note.storage') }}</span>
              <select
                  v-model="selectedStorageId"
                  class="form-select form-select-sm"
                  :disabled="storagesLoading || storagesFetchError || !!loading"
              >
                  <option :value="null">
                      {{ $t('materialstorage.select_storage') }}
                  </option>
                  <option
                      v-for="storage in storages"
                      :key="storage.id"
                      :value="storage.id"
                  >
                      {{ storage.name }}
                  </option>
              </select>
              <div
                  v-if="storagesFetchError"
                  class="alert alert-danger d-flex align-items-center gap-2 mt-1"
              >
                  {{ $t('delivery_note.storage_fetch_error') }}
                  <button
                      class="btn btn-sm btn-outline-danger"
                      @click.stop="fetchStorages"
                  >
                      {{ $t('materialstorage.retry') }}
                  </button>
              </div>
              <div
                  v-if="storagesLoading"
                  class="spinner-border spinner-border-sm mt-1"
                  role="status"
                  aria-hidden="true"
              />
          </div>
          <div class="col-sm-6">
            <span class="span-title">{{ $t('delivery_note.material') }}</span>
              <select
                  v-model="noteUpdate.storage_material.id"
                  class="form-select form-select-sm"
                  :disabled="!selectedStorage || !!loading"
              >
                  <option :value="-1">
                      {{ $t('delivery_note.form.select_material') }}
                  </option>
                  <option
                      v-for="record in selectedStorageMaterials"
                      :key="record.id"
                      :value="record.id"
                  >
                      {{ record.name }}
                  </option>
              </select>

            <div class="row mt-3">
              <div class="col-7">
                  <input
                      type="number"
                      class="form-control form-control-sm"
                      :value="noteUpdate.amount"
                      step="0.01"
                      :disabled="noteUpdate.storage_material.id < 0 || !!loading"
                      @input="i => noteUpdate.amount = Number(i.target.value)"
                  />
              </div>
              <div class="col-5">
                  <select
                      v-model="unit"
                      class="form-select form-select-sm"
                      :disabled="noteUpdate.storage_material.id < 0 || !!loading"
                  >
                      <option :value="-1">
                          {{ $t('delivery_note.form.select_unit') }}
                      </option>
                      <option
                          v-for="unitObj in units"
                          :key="unitObj.value"
                          :value="unitObj.value"
                      >
                          {{ unitObj.value }}
                      </option>
                  </select>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="showStorageBlock && editOld"
          class="row"
        >
          <div class="col-sm-6">
            <span class="span-title">{{ $t('delivery_note.material') }}</span>
            <span>{{ storageMaterialFullDisplayName }}</span>
          </div>
          <div class="col-sm-6">
            <span class="span-title">{{ $t('delivery_note.amount') }}</span>
            <div class="row">
              <div class="col-7">
                  <input
                      type="number"
                      class="form-control form-control-sm"
                      :value="noteUpdate.amount"
                      step="0.01"
                      :disabled="!!loading"
                      @input="i => noteUpdate.amount = Number(i.target.value)"
                  />
              </div>
              <div class="col-5">
                  <select
                      v-model="unit"
                      class="form-select form-select-sm"
                      :disabled="!!loading"
                  >
                      <option :value="-1">
                          {{ $t('delivery_note.form.select_unit') }}
                      </option>
                      <option
                          v-for="unitObj in units"
                          :key="unitObj.value"
                          :value="unitObj.value"
                      >
                          {{ unitObj.value }}
                      </option>
                  </select>
              </div>
            </div>
          </div>
        </div>
          <div v-if="!editOld" class="row">
          <!-- Vehicle dropdown -->
          <div
              class="col-sm-6"
          >
            <span class="span-title">{{ $t('trip_list.vehicle') }}</span>
            <multiselect
              v-if="!noteUpdate.vehicle.license_plate"
              v-model="vehicle"
              class="multiselect_routa"
              :placeholder="$t('trip_list.select_vehicle')"
              :deselect-label="$t('common.remove_selection')"
              :select-label="$t('common.select')"
              :close-on-select="true"
              label="name"
              track-by="name"
              :searchable="true"
              :internal-search="true"
              :selected-label="$t('common.selected')"
              :options="vehicleOptions"
              :loading="vehiclesLoading"
              :disabled="vehiclesLoading"
            >
              <template
                #singleLabel="props"
              >
                <span class="option__desc">
                  <span class="option__title">{{ props.option.name }}</span>
                </span>
              </template>
              <template
                #option="props"
              >
                <div class="option__desc">
                  <span class="option__title multiselect_detail_block">{{ props.option.name }}</span>
                </div>
              </template>
              <template #noResult>
                <span>{{ $t('common.not_found') }}</span>
              </template>
              <template #noOptions>
                <span>{{ $t('common.no_results') }}</span>
              </template>
            </multiselect>

              <!-- Only truck vehicle types -->
              <div class="form-check mt-1">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      id="onlyTrucks"
                      v-model="onlyTrucks"
                      :disabled="loading"
                  />
                  <label class="form-check-label" style="font-size: .9em" for="onlyTrucks">
                      {{ $t('delivery_note.only_trucks') }}
                  </label>
              </div>

          </div>
          <!-- License plate for outside organization vehicles -->
          <div class="col-sm-6">
            <span class="span-title">{{ $t('vehicle_defects.license_plate') }}</span>
              <input
                  v-if="!vehicle && noteUpdate.vehicle"
                  v-model="noteUpdate.vehicle.license_plate"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="ABC-123"
                  :disabled="!!loading"
              />
          </div>
          </div>
          <div v-if="!editOld" class="row px-2">
              <div class="info-box mt-4 col-lg-5 col-md-12 col-sm-12 ms-2">
                  <font-awesome-icon icon="info-circle" class="info-circle" />
                  <span>
                      {{ $t("delivery_note.vehicle_selection_tip") }} <br>
                      <span style="font-weight: bold">{{ $t("delivery_note.vehicle_selection_tip_2") }}</span>
                  </span>
              </div>
          </div>
          <div v-if="editOld" class="row">
              <div class="col-sm-6">
                  <span class="span-title">{{ $t('delivery_note.status') }}</span>
                  <select
                      v-model="noteUpdate.status"
                      class="form-select form-select-sm"
                      :disabled="!!loading"
                  >
                      <option
                          v-for="status in getStatus()"
                          :key="status.value"
                          :value="status.value"
                      >
                          {{ getStatusString(status.value) }}
                      </option>
                  </select>
              </div>
          </div>

          <div class="col-sm-12 button-container pb-2">
          <button
            type="reset"
            class="btn btn-sm btn-primary form-button"
          >
            {{ editOld ? $t('common.back') : $t('delivery_note.clear') }}
          </button>
          <button
            type="submit"
            class="btn btn-sm btn-success form-button"
            :disabled="!!loading"
          >
            {{ editOld ? $t('common.save') : $t('delivery_note.add') }}
          </button>
        </div>
      </form>
    </div>
    <div
      v-if="!!loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {deliveryNoteHelper, units} from "@/components/mixins/DeliveryNoteMixin";
import {restApi} from "@/components/mixins/RestApiMixin";
import {orderMixin} from "@/components/mixins/OrderMixin";
import {vehicleTypes} from "@/components/mixins/VehicleMixin";
import DateTimePicker from "@/components/common/DateTimePicker";
import {inject} from "vue";
import ContractOrderMultiselect from "../order/ContractOrderMultiselect.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

export default {
    name: "DeliveryNoteEditor",
    components: {FontAwesomeIcon, ContractOrderMultiselect, DateTimePicker},
    mixins: [deliveryNoteHelper, restApi, orderMixin, vehicleTypes],
    props: {
        note: {
            type: Object,
            default: null
        },
        station: {
            type: Number,
            default: null
        },
        contracts: {
            type: Array,
            default() {
                return []
            }
        },
        materials: {
            type: Array,
            default() {
                return []
            }
        },
        setCreated: {
            type: Boolean,
            default: true
        },
        showOnlyMyContractsOption: {
            type: Boolean,
            default: false
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data() {
        return {
            noteUpdate: null,
            contract: null,
            order: null,
            vehicleOptions: [],
            onlyTrucks: true,
            vehicle: undefined, // Dropdown selection
            units: [
                {value: units.TONS},
                {value: units.CUBIC_METERS}
            ],
            unit: null,
            loading: 0,
            vehiclesLoading: false,
            createdTime: null,
            onlyUserOwnedContracts: false,
            storages: [],
            selectedStorageId: null,
            storagesLoading: false,
            storagesFetchError: false
        }
    },
    computed: {
        editOld: function () {
            return this.noteUpdate && this.noteUpdate.id > 0
        },
        selectedStorage: function () {
            return this.storages.find(s => s.id === this.selectedStorageId) || null
        },
        selectedStorageMaterials: function () {
            return this.selectedStorage
                ? this.selectedStorage.material_records.filter(r => r.active === true)
                : []
        },
        storageMaterialFullDisplayName: function () {
            if (!this.noteUpdate || !this.noteUpdate.storage_material) {
                return ''
            }
            const materialName = this.noteUpdate.storage_material.name || ''
            const storageName = this.noteUpdate.storage_material.storage ? this.noteUpdate.storage_material.storage.name : ''
            return storageName ? materialName + ' (' + storageName + ')' : materialName
        },
        showStationBlock: function () {
            if (this.editOld) {
                return !!(this.noteUpdate.station_material && this.noteUpdate.station_material.id >= 0)
            }
            return !!this.station
        },
        showStorageBlock: function () {
            return !this.showStationBlock
        }
    },
    watch: {
        onlyUserOwnedContracts: function() {
            this.$emit('onlyUserOwnedContractSelected', this.onlyUserOwnedContracts, "editor")
        },
        station: function () {
            if (!this.editOld) {
                this.onReset()
            }
        },
        order: function () {
            this.noteUpdate.order = this.order ?  {id: this.order.id} : {id: -1}
        },
        onlyTrucks() {
            this.fetchVehicles()
        },
        selectedStorageId: function (newId, oldId) {
            if (newId !== oldId) {
                this.noteUpdate.storage_material = {id: -1}
            }
        }
    },
    mounted() {
        this.initValues();
        // When editing an existing note, initValues() already triggers fetchVehicles()
        // via selectContract(). Only fetch here for the "new note" case.
        if (!this.editOld) {
            this.fetchVehicles()
        }
        if (this.showStorageBlock && !this.editOld) {
            this.fetchStorages()
        }
    },
    methods: {
        setOrder(order) {
            this.order = Array.isArray(order) && order.length > 0 ? order[0] : order
        },
        normalizeNoteUpdate() {
            if (!this.noteUpdate.station_material) {
                this.noteUpdate.station_material = {id: -1}
            }
            if (!this.noteUpdate.storage_material) {
                this.noteUpdate.storage_material = {id: -1}
            }
        },
        initValues() {
            this.noteUpdate = this.jsonToItem(this.note)
            this.normalizeNoteUpdate()

            if (this.noteUpdate.unit) {
                this.unit = this.noteUpdate.unit
            }
            if (this.noteUpdate.order) {
                this.order = this.noteUpdate.order
                this.contract = this.noteUpdate.order && this.noteUpdate.contract ? this.noteUpdate.order.contract.id : null
            }
            if (this.noteUpdate.vehicle.license_plate !== '') {
                this.vehicle = this.noteUpdate.vehicle
            }
            if (this.noteUpdate.created) {
                this.createdTime = this.noteUpdate.created
            } else {
                // Init as current time
                this.createdTime = new Date()
            }
            if (this.editOld) {
                this.contract = this.note.order.contract.id
                this.selectContract(this.contract, false)
            } else {
                if (this.contract) {
                    this.noteUpdate.contract = {id: this.contract}
                }
                if (this.order) {
                    this.noteUpdate.order = {id: this.order}
                }
                if (this.vehicle) {
                    this.noteUpdate.vehicle = {id: this.vehicle}
                }
            }
        },

        // Get vehicles for dropdown menu
        fetchVehicles: function () {
            this.vehicleOptions = []
            this.vehiclesLoading = true
            if (this.contract) {
                const contractObject = this.contracts.find(item => item.id === this.contract)
                let params = {
                    contract: this.contract,
                    from: contractObject ? contractObject.start_date : this.note.order.contract.start_date
                }
                this.restFetchParams(this.tripVehicleUrl, params, this.handleVehicles)
            } else {
                this.restFetch(this.vehicleUrl, this.handleVehicles)
            }
        },

        handleVehicles: function (response) {
            this.vehiclesLoading = false
            if (response && response.data) {
                response.data.forEach(item => {
                    if (!this.onlyTrucks || item.vehicle_type === vehicleTypes.TRUCK) {
                        this.vehicleOptions.push({
                            value: item.id,
                            name: item.make + ' ' + item.vehicle_model + ' - ' + item.license_plate,
                            license_plate: item.license_plate
                        })
                    }
                })
                this.fetchDeliveryVehicles()
            }
        },

        fetchDeliveryVehicles() {
            if (this.contract) {
                const contractObject = this.contracts.find(item => item.id === this.contract)
                let params = {
                    contract: this.contract
                }
                this.restFetchParams(this.deliveryVehicleUrl, params, this.handleDeliveryVehicles)
            }
        },

        handleDeliveryVehicles: function (response) {
            this.vehiclesLoading = false
            if (response && response.data) {
                response.data.forEach(item => {
                    // Add to options only if it is not already in options
                    const existing = this.vehicleOptions.find(vehicle => vehicle.value === item.id)
                    if (!existing && (!this.onlyTrucks || item.vehicle_type === vehicleTypes.TRUCK)) {
                        this.vehicleOptions.push({
                            value: item.id,
                            name: item.make + ' ' + item.vehicle_model + ' - ' + item.license_plate,
                            license_plate: item.license_plate
                        })
                    }
                })

                // Last sort options by make, model and license_plate
                this.vehicleOptions.sort((a, b) => {
                    const nameCompare = a.name.localeCompare(b.name);
                    if (nameCompare !== 0) {
                        return nameCompare;
                    }
                    return a.license_plate.localeCompare(b.license_plate);
                });
            }
        },

        onReset: function () {
            this.noteUpdate = this.jsonToItem(this.note)
            this.normalizeNoteUpdate()
            this.contract = null
            this.onlyUserOwnedContracts = 0
            this.order = null
            this.createdTime = new Date()
            if (this.editOld) {
                this.$emit('close')
            }
        },
        onSubmit: function () {
            this.noteUpdate.unit = this.unit
            let jsonNote = this.validateInput(this.noteUpdate)
            if (jsonNote !== undefined) {
                this.loading++
                if (!this.editOld) {
                    this.restAdd(this.materialDeliveryUrl, jsonNote, this.addSuccess, this.fail)
                } else {
                    this.restUpdate(this.materialDeliveryUrl, jsonNote, this.addSuccess, this.fail)
                }
            }
        },
        validateInput: function (originalNote) {
            let note = JSON.parse(JSON.stringify(originalNote))
            if (!note.order.id || note.order.id < 0) {
                this.showAlert(this.$t('delivery_note.form_validation.order'))
                return undefined
            }
            // Must be either station or storage material linked!
            if ((!note.station_material || note.station_material.id < 0) && (
                !note.storage_material || note.storage_material.id < 0 )) {
                this.showAlert(this.$t('delivery_note.form_validation.material'))
                return undefined
            }
            if (note.amount === '' || note.unit < 0) {
                this.showAlert(this.$t('delivery_note.form_validation.amount_unit'))
                return undefined
            }
            if (this.vehicle && this.vehicle.license_plate && this.vehicle.license_plate !== '') {
                note.vehicle = this.vehicle
            } else if (this.noteUpdate.vehicle) {
                note.vehicle = this.noteUpdate.vehicle
            } else {
                this.showAlert(this.$t('delivery_note.form_validation.vehicle'))
                return undefined
            }
            if (this.setCreated && !this.createdTime) {
                this.showAlert(this.$t('delivery_note.form_validation.created'))
                return undefined
            } else if (this.setCreated) {
                note.created = this.createdTime
            }
            if (note.storage_material && note.storage_material.id >= 0) {
                note.storage_material = {id: note.storage_material.id}
                note.station_material = null
            } else if(note.station_material && note.station_material.id >= 0) {
                note.storage_material = null
                note.station_material = {id: note.station_material.id}
            }
            return note
        },
        addSuccess: function () {
            this.loading--
            if (!this.editOld) {
                this.showAlert(this.$t('common.alert_add_success'), 'success')
                // Keep form info (except vehicle and amount) for following notes
                this.vehicle = null
                this.noteUpdate.vehicle = {license_plate: null}
                this.noteUpdate.amount = ''
                this.createdTime = new Date()
                this.$emit('update', true)
            } else {
                this.showAlert(this.$t('common.alert_update_success'), 'success')
                this.$emit('update')
                this.onReset()
            }
        },
        fail: function () {
            this.loading--
            if (!this.editOld) {
                this.showAlert(this.$t('common.alert_add_failed'))
            } else {
                this.showAlert(this.$t('common.alert_update_failed'))
            }
        },
        selectContract: function (id, update = true) {
            if (!this.editOld || (this.editOld && update)) {
                this.noteUpdate.order.id = -1
            }
            this.fetchVehicles()
        },
        updateVehicles() {
            this.fetchVehicles()
        },
        fetchStorages: function () {
            this.storagesLoading = true
            this.storagesFetchError = false
            this.restFetch(this.materialStorageUrl, this.handleStorages, this.handleStoragesFail)
        },
        handleStorages: function (response) {
            this.storagesLoading = false
            this.storages = response && response.data ? response.data : []
        },
        handleStoragesFail: function () {
            this.storagesLoading = false
            this.storagesFetchError = true
        }
    }
}
</script>

<style lang="scss" scoped>
:deep(.multiselect__spinner) {
    top: 50%;
    transform: translateY(-50%);
}
</style>
